import { QCONTENT_ORDER_TYPE } from '@exam-management/common/utils';
import {
  FilterOperatorEnum,
  QContentAuditLogIdentifierType,
  useSearchQContentAuditLogsQuery,
} from '@exam-management/graphql';
import {
  DataGrid,
  Flex,
  type GridSortModel,
  InfoOutlinedIcon,
  NoRowsOverlay,
  PaperSection,
} from '@shared/components';

import { useQContentActivityLogSearchParams } from '../../hooks';

import { columns } from './column';

export default function QContentActivityLogList({
  questionId,
  questionGroupId,
}: {
  questionId?: string | null;
  questionGroupId?: string | null;
}) {
  const [{ search, page, pageSize, sort, field }, setSearchParams] =
    useQContentActivityLogSearchParams();

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'actionTime';
    const sort = sorting[0]?.sort ?? 'desc';
    setSearchParams({ sort: sort, field: field });
  };

  const jsonContainsFilterValues = questionId
    ? {
        identifierId: questionId,
        identifierType: QContentAuditLogIdentifierType.QcontentId,
      }
    : questionGroupId
      ? {
          identifierId: questionGroupId,
          identifierType: QContentAuditLogIdentifierType.QuestionGroupId,
        }
      : null;

  const { data, loading } = useSearchQContentAuditLogsQuery({
    variables: {
      input: {
        order: {
          field,
          type: QCONTENT_ORDER_TYPE[sort as keyof typeof QCONTENT_ORDER_TYPE],
        },
        filter: {
          searchText: {
            fields: ['detail'],
            value: search,
          },
          conditions: [
            {
              field: 'executionResult',
              operator: FilterOperatorEnum.Equal,
              value: 'SUCCESS',
            },
          ],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        jsonContainsFilter: jsonContainsFilterValues
          ? {
              field: 'identifiers',
              value: jsonContainsFilterValues,
            }
          : null,
      },
    },
    fetchPolicy: 'network-only',
  });

  const total = data?.qctSearchQContentAuditLogs.total || 0;

  const isSearchResultEmpty = total === 0 && search.trim() !== '';

  return (
    <PaperSection title='Activity Log'>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(6),
        })}
      >
        <DataGrid
          rows={data?.qctSearchQContentAuditLogs.qContentAuditLogs || []}
          columns={columns}
          getRowHeight={() => 'auto'}
          columnHeaderHeight={48}
          disableColumnMenu
          sortingMode='server'
          onSortModelChange={handleSortModelChange}
          loading={loading}
          slots={{
            noRowsOverlay: () =>
              !isSearchResultEmpty ? (
                <NoRowsOverlay icon={InfoOutlinedIcon} title='ยังไม่มีข้อมูล Activity Log' />
              ) : (
                <NoRowsOverlay />
              ),
          }}
          slotProps={{
            footer: {
              total,
              pagination: {
                page: page,
                count: total,
                rowsPerPage: pageSize,
                onPageChange: (_, newPage: number) => {
                  setSearchParams({ search, page: newPage });
                },
                onRowsPerPageChange: (event) => {
                  const newPageSize = parseInt(event.target.value, 10);
                  setSearchParams({ search, page: 0, pageSize: newPageSize });
                },
              },
            },
          }}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.spacing(2),
            border: `1px solid ${theme.palette.grey[300]}`,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            },
            '& .MuiDataGrid-row .MuiDataGrid-cell': {
              padding: theme.spacing(3),
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              ...theme.typography.body2,
            },
            '& .MuiDataGrid-columnHeader': {
              px: 3,
            },
            '--DataGrid-overlayHeight': '400px',
          })}
        />
      </Flex>
    </PaperSection>
  );
}
